import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, MapPinned, Plus, History, Tag, Building2, TrendingUp, Sparkles,
} from "lucide-react";
import { api, STAGES } from "../lib/api";
import { PageHeader, Stat, Spinner, EmptyState } from "../components/UI";
import StageBadge from "../components/StageBadge";
import { fmtDateShort } from "../lib/utils";

export default function Dashboard() {
  const [pipeline, setPipeline] = useState(null);
  const [weekly, setWeekly] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [p, w, e] = await Promise.all([
          api.get("/analytics/pipeline"),
          api.get("/analytics/weekly"),
          api.get("/establishments", { params: { limit: 5 } }),
        ]);
        setPipeline(p.data);
        setWeekly(w.data);
        setRecent(e.data.items || []);
      } catch (err) {
        // Best-effort: dashboard widgets shouldn't take down the page if one
        // analytics call fails. Log so the failure isn't completely silent.
        console.warn("Dashboard data load failed:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div>
      <PageHeader
        testid="dashboard-header"
        title="Welcome back."
        subtitle="The Literally Spirits sales floor — your accounts, your week, your wins."
        actions={
          <>
            <Link to="/search" data-testid="dashboard-discover-btn" className="btn-outline">
              <MapPinned size={16} /> Discover nearby
            </Link>
            <Link to="/establishments/new" data-testid="dashboard-add-establishment-btn" className="btn-primary">
              <Plus size={16} /> New establishment
            </Link>
          </>
        }
      />

      {loading ? (
        <Spinner />
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Stat
              label="Accounts"
              value={pipeline?.total ?? 0}
              hint="In your book of business"
            />
            <Stat
              label="Closed"
              value={pipeline?.closed ?? 0}
              hint={`${pipeline?.conversion_rate ?? 0}% conversion`}
              accent="text-stage-closedFg"
            />
            <Stat
              label="Visits this week"
              value={weekly?.this_week_total ?? 0}
              hint={
                weekly?.delta != null
                  ? `${weekly.delta >= 0 ? "+" : ""}${weekly.delta} vs last week`
                  : ""
              }
              accent={weekly?.delta >= 0 ? "text-stage-closedFg" : "text-stage-lostFg"}
            />
            <Stat
              label="In active pipeline"
              value={
                (pipeline?.stages || [])
                  .filter((s) => ["qualified", "demo", "proposal"].includes(s.stage))
                  .reduce((acc, s) => acc + s.count, 0)
              }
              hint="Qualified → Proposal"
            />
          </div>

          {/* Pipeline overview */}
          <div className="grid md:grid-cols-3 gap-4 mt-6">
            <div className="card p-6 md:col-span-2">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="label">Pipeline</div>
                  <h3 className="font-display text-2xl text-brand-ink">Stage breakdown</h3>
                </div>
                <Link to="/analytics" className="btn-ghost text-sm">
                  Details <ArrowRight size={14} />
                </Link>
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-3">
                {STAGES.map((s) => {
                  const count = pipeline?.stages?.find((x) => x.stage === s.value)?.count || 0;
                  return (
                    <div
                      key={s.value}
                      className="rounded-xl border border-brand-border p-3"
                      data-testid={`dash-stage-${s.value}`}
                    >
                      <StageBadge stage={s.value} />
                      <div className="font-display text-2xl mt-2 text-brand-ink">{count}</div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="card p-6">
              <div className="label">Get moving</div>
              <h3 className="font-display text-2xl text-brand-ink">Quick actions</h3>
              <div className="flex flex-col gap-2 mt-4">
                <Link to="/visits/new" className="btn-outline justify-start" data-testid="quick-log-visit">
                  <History size={16} /> Log a visit
                </Link>
                <Link to="/brands" className="btn-outline justify-start" data-testid="quick-manage-brands">
                  <Tag size={16} /> Manage brands
                </Link>
                <Link to="/establishments" className="btn-outline justify-start" data-testid="quick-view-pipeline">
                  <Building2 size={16} /> Open pipeline
                </Link>
              </div>
            </div>
          </div>

          {/* Recent establishments */}
          <div className="card p-6 mt-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="label">Latest</div>
                <h3 className="font-display text-2xl text-brand-ink">Recently added accounts</h3>
              </div>
              <Link to="/establishments" className="btn-ghost text-sm" data-testid="dash-view-all-estab">
                View all <ArrowRight size={14} />
              </Link>
            </div>

            {recent.length === 0 ? (
              <EmptyState
                testid="dash-empty-recent"
                icon={Sparkles}
                title="Add your first account"
                body="Capture restaurants, bars, or stores you want your brand into."
                action={
                  <Link to="/establishments/new" className="btn-primary">
                    <Plus size={16} /> New establishment
                  </Link>
                }
              />
            ) : (
              <div className="divide-y divide-brand-border">
                {recent.map((e) => (
                  <Link
                    to={`/establishments/${e.id}`}
                    key={e.id}
                    className="flex items-center justify-between py-3 hover:bg-brand-muted/50 rounded-lg px-2 -mx-2 transition-colors"
                    data-testid={`dash-recent-${e.id}`}
                  >
                    <div>
                      <div className="font-medium text-brand-ink">{e.name}</div>
                      <div className="text-sm text-brand-mutedFg">
                        {[e.city, e.state].filter(Boolean).join(", ") || "—"} · added {fmtDateShort(e.created_at)}
                      </div>
                    </div>
                    <StageBadge stage={e.pipeline_stage} />
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Launch banner — single source of truth for product name + version */}
          <div className="mt-8 flex items-center justify-center gap-2 text-[11px] text-brand-mutedFg" data-testid="launch-banner">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-brand-gold" />
            <span className="uppercase tracking-[0.18em]">Built for Literally Spirits</span>
            <span className="opacity-50">·</span>
            <span className="font-medium text-brand-ink2">v1.0</span>
          </div>
        </>
      )}
    </div>
  );
}
